import api from './api';
import type {
  CompanyConfigurations,
  ConfigurationStatus,
  IntegrationProvider,
  AuthNetCredentials,
  MdiCredentials,
  CurexaCredentials,
  PcpHealthCredentials,
  GoogleAnalyticsCredentials,
  ApiKeyResponse,
  EmailDomainConfig,
} from '../types';

type CredentialsMap = {
  authNet: Partial<AuthNetCredentials>;
  mdi: Partial<MdiCredentials>;
  curexa: Partial<CurexaCredentials>;
  pcpHealth: Partial<PcpHealthCredentials>;
  googleAnalytics: Partial<GoogleAnalyticsCredentials>;
};

export const configurationService = {
  /**
   * Get all company configurations (integration credentials and API key)
   * Credentials are returned masked for security
   */
  async getConfigurations(): Promise<CompanyConfigurations> {
    const response = await api.get<CompanyConfigurations>('/company/configurations');
    return response.data;
  },

  /**
   * Get configuration status for onboarding stepper
   */
  async getConfigurationStatus(): Promise<ConfigurationStatus> {
    const response = await api.get<ConfigurationStatus>('/company/configurations/status');
    return response.data;
  },

  /**
   * Update integration credentials for a specific provider
   */
  async updateIntegrationCredentials<T extends IntegrationProvider>(
    provider: T,
    credentials: CredentialsMap[T]
  ): Promise<{ success: boolean; provider: T; credentials: CredentialsMap[T] }> {
    const response = await api.put<{ success: boolean; provider: T; credentials: CredentialsMap[T] }>(
      '/company/configurations/integrations',
      { provider, credentials }
    );
    return response.data;
  },

  /**
   * Enable API access and get a new API key
   * Note: The full API key is only shown once after enabling
   */
  async enableApiAccess(): Promise<ApiKeyResponse> {
    const response = await api.post<ApiKeyResponse>('/company/configurations/api-access/enable');
    return response.data;
  },

  /**
   * Rotate the API key and get a new one
   * Note: The full API key is only shown once after rotation
   */
  async rotateApiKey(): Promise<ApiKeyResponse> {
    const response = await api.post<ApiKeyResponse>('/company/configurations/api-access/rotate');
    return response.data;
  },

  /**
   * Revoke API access entirely
   */
  async revokeApiAccess(): Promise<{ success: boolean; message: string }> {
    const response = await api.post<{ success: boolean; message: string }>(
      '/company/configurations/api-access/revoke'
    );
    return response.data;
  },

  /**
   * Get email domain configuration
   */
  async getEmailDomain(): Promise<EmailDomainConfig | null> {
    const response = await api.get<EmailDomainConfig | null>('/company/email-domain');
    return response.data;
  },

  /**
   * Create or update email domain configuration
   */
  async createEmailDomain(domain: string): Promise<EmailDomainConfig> {
    const response = await api.post<EmailDomainConfig>('/company/email-domain', { domain });
    return response.data;
  },

  /**
   * Refresh verification status from AWS SES
   */
  async verifyEmailDomain(): Promise<EmailDomainConfig> {
    const response = await api.post<EmailDomainConfig>('/company/email-domain/verify');
    return response.data;
  },

  /**
   * Delete email domain configuration
   */
  async deleteEmailDomain(): Promise<{ success: boolean }> {
    const response = await api.delete<{ success: boolean }>('/company/email-domain');
    return response.data;
  },
};

